import {
  DataRoomMember,
  DataRoomMemberRole,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { Question } from 'router/subrouters/DataRoom/models/QAndA';
import { ObjectValues } from 'types';

export type DataRoomReport = {
  _id: string;
  dataRoomNodeId: string;
  createdAt: Date;
  action: DataRoomReportAction;
  memberName: string;
  memberEmail: string;
  memberRole: DataRoomMemberRole;
  documentName: string;

  shareableLinkName: string;
  rfiName: string;

  // for renamed document action
  oldDocumentName: string;

  // for access granted and access revoked
  changedAccessMemberName: string;
  changedAccessMemberRole: DataRoomMemberRole;
  changedAccessMemberEmail: string;

  // for time spent on the platform action
  timeSpent: number;
  dataRoomQuestion?: Question;

  memberIpAddress?: string;

  fileUploadErrorCode: FileUploadErrorCode;
};

export type ReportFilterType = {
  page?: string;
  action?: string;
  qaAction?: string;
  memberRole?: string;
  dataRoomMemberId?: string;
  from?: string;
  to?: string;
};

export const REPORT_FILE_ACTION_TYPE = {
  LOG_IN: 'log_in',
  LOG_OUT: 'log_out',
  DOCUMENT_VIEWED: 'document_viewed',
  DOCUMENT_DOWNLOADED: 'document_downloaded',
  DOCUMENT_PUBLISHED: 'document_published',
  DOCUMENT_RENAMED: 'document_renamed',
  DOCUMENT_DELETED: 'document_deleted',
  ACCESS_GRANTED: 'access_granted',
  ACCESS_REVOKED: 'access_revoked',
  TIME_SPENT: 'time_spent',
  SECURE_LINK_TERMS_AND_CONDITIONS_ACCEPTED:
    'secure_link_terms_and_conditions_accepted',
  DOCUMENT_UPLOAD_FAILED: 'document_upload_failed',
};

export const QA_REPORT_ACTIONS = {
  DRAFTED_QUESTION: 'drafted_question', //someone on buy side create question
  BUY_SIDE_RETURNED_QUESTION: 'buy_side_returned_question', //someone on buy side reject question
  SUBMITTED_QUESTION: 'submitted_question', //someone on buy side approve question
  SELL_SIDE_ACCEPTED_QUESTION: 'sell_side_accepted_question', //someone on sell side accept question
  SELL_SIDE_REJECTED_QUESTION: 'sell_side_rejected_question', //someone on sell side reject question
  DRAFTED_ANSWER: 'drafted_answer',
  SELL_SIDE_APPROVED_ANSWER: 'sell_side_approved_answer',
  SELL_SIDE_REJECTED_ANSWER: 'sell_side_rejected_answer',
  FAQ_PUBLISHED: 'faq_published',
};

export const SETTINGS_REPORT_ACTIONS = {
  CHANGED_SECURITY_SETTINGS_FOR_FILE: 'changed_security_settings_for_file',
  CHANGED_SECURITY_SETTINGS_FOR_DATA_ROOM:
    'changed_security_settings_for_data_room',
  CHANGED_SECURITY_SETTINGS_FOR_SHAREABLE_LINK:
    'changed_security_settings_for_shareable_link',
  CHANGED_SECURITY_SETTINGS_FOR_RFI: 'changed_security_settings_for_rfi',
};

export const REPORT_ACTION_TYPE = {
  ...REPORT_FILE_ACTION_TYPE,
  ...QA_REPORT_ACTIONS,
  ...SETTINGS_REPORT_ACTIONS,
};

export const ERROR_REPORT_ACTIONS = [
  REPORT_FILE_ACTION_TYPE.DOCUMENT_UPLOAD_FAILED,
];

export type DataRoomReportAction = ObjectValues<typeof REPORT_ACTION_TYPE>;

export type DataRoomQAAction = ObjectValues<typeof QA_REPORT_ACTIONS>;

export type SettingsReportAction = ObjectValues<typeof SETTINGS_REPORT_ACTIONS>;

export type DataRoomNodeReport = {
  _id: string;

  // Analytics
  numberOfViews: number;
  numberOfDownloads: number;
  numberOfSecondsSpent: number;

  // Match by
  dataRoomId: string;

  dataRoomNodeId: string;
  dataRoomNode?: DataRoomNode;

  dataRoomMemberId: string;
  dataRoomMember?: Partial<DataRoomMember>;

  // Quick access for member role (We track stats for every data room member but want to show buyer only for now)
  dataRoomMemberRole: DataRoomMemberRole;

  // Mongo defined properties
  createdAt: Date;
  updatedAt: Date;
};

export type TrackFileUploadErrorPayload = {
  errorCode: FileUploadErrorCode;
  documentName?: string;
};

export const FILE_UPLOAD_ERROR_CODE = {
  FILE_INVALID_TYPE: 1,
  FILE_TOO_LARGE: 2,
  FILE_CANNOT_ADD_SECURITY_LAYERS: 3,
  MISSING_FILE_NAME: 4,
  INVALID_FILE_NAME: 5,
  DR_UPLOAD_STORAGE_FULL: 6,
} as const;

export type FileUploadErrorCode = ObjectValues<typeof FILE_UPLOAD_ERROR_CODE>;
